import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { Cross, ExpandLeft, ExpandRight } from "components/icons/generated";
import ButtonIcon from "ds/components/ButtonIcon";
import MainNavigationMenuItem from "components/MainNavigation/components/elements/MenuItem";
import useMainNavigationFadeTransition from "components/MainNavigation/hooks/useFadeTransition";
import useMainNavigationStore from "components/MainNavigation/stores/useMainNavigationStore";
import { selectNavigationShowsExpandedContent } from "components/MainNavigation/selectors/navigationState";

const MainNavigationButtonExpand = () => {
  const {
    showsExpandedContent,
    isSidebarExpanded,
    setIsSidebarExpanded,
    setIsSidebarInPreview,
    mobileFullScreen,
    setMobileFullScreen,
    closeDropdownMenu,
  } = useMainNavigationStore(
    useShallow((state) => ({
      showsExpandedContent: selectNavigationShowsExpandedContent(state),
      isSidebarExpanded: state.isSidebarExpanded,
      setIsSidebarExpanded: state.setIsSidebarExpanded,
      setIsSidebarInPreview: state.setIsSidebarInPreview,
      mobileFullScreen: state.mobileFullScreen,
      setMobileFullScreen: state.setMobileFullScreen,
      closeDropdownMenu: state.closeDropdownMenu,
    }))
  );

  const { transitionClasses } = useMainNavigationFadeTransition(!!showsExpandedContent);

  const handleToggleSidebarExpanded = useCallback(() => {
    setIsSidebarExpanded(!isSidebarExpanded);
    setIsSidebarInPreview(false);
  }, [isSidebarExpanded, setIsSidebarExpanded, setIsSidebarInPreview]);

  const handleExitMobileFullScreen = useCallback(() => {
    setMobileFullScreen(false);
    closeDropdownMenu();
  }, [setMobileFullScreen, closeDropdownMenu]);

  const menuItem = useMemo(
    () => ({
      id: "expand",
      title: "Expand",
      icon: ExpandRight,
      onAction: handleToggleSidebarExpanded,
    }),
    [handleToggleSidebarExpanded]
  );

  if (mobileFullScreen) {
    return (
      <ButtonIcon
        icon={Cross}
        onPress={handleExitMobileFullScreen}
        variant="ghost"
        size="small"
        className={transitionClasses}
      >
        Close
      </ButtonIcon>
    );
  }

  if (isSidebarExpanded) {
    return (
      <ButtonIcon
        icon={ExpandLeft}
        onPress={handleToggleSidebarExpanded}
        variant="ghost"
        size="small"
        className={transitionClasses}
      >
        Collapse
      </ButtonIcon>
    );
  }

  if (showsExpandedContent) {
    return (
      <ButtonIcon
        icon={ExpandRight}
        onPress={handleToggleSidebarExpanded}
        variant="ghost"
        size="small"
        className={transitionClasses}
      >
        Expand
      </ButtonIcon>
    );
  }

  return <MainNavigationMenuItem item={menuItem} transitionClasses={transitionClasses} />;
};

export default MainNavigationButtonExpand;
